import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  ApiClientError,
  ORAL_EXAM_JOIN_EXPECTED_ERROR_CODES,
  useApiErrorReporter,
} from '@/common/utils/apiErrorReporter';
import { getJoinErrorCode } from '@/common/utils/oralExam/joinErrorCode';
import {
  readCachedJoinCreds,
  writeCachedJoinCreds,
} from '@/common/components/meeting/meetingSessionPersistence';
import { getLiveKitJoinCacheKey } from '@/common/utils/oralExam/liveKitJoin';
import type { LiveKitJoinHandle } from '@/student/module/hooks/useEventTaskSlots';

type LiveKitJoinCreds = {
  token: string;
  livekitUrl: string;
  roomName?: string;
  expiresAt?: string;
};

/** Remint this long before JWT expiry so stay TTL can extend the session. */
export const REMINT_BEFORE_EXPIRY_MS = 60_000;

/** User retry buttons ignore presses while a token request is pending and younger than this. */
export const RETRY_THROTTLE_MS = 15_000;

/** Backoff between in-session token remint retries after network/server failures. */
export const TRANSIENT_TOKEN_RETRY_DELAYS_MS = [2000, 5000, 10_000, 20_000] as const;

function getHttpStatus(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const status = (err as { response?: { status?: number } }).response?.status;
  return typeof status === 'number' ? status : undefined;
}

function isApiClientError(err: unknown): err is ApiClientError {
  if (typeof ApiClientError === 'function' && err instanceof ApiClientError) {
    return true;
  }
  return typeof err === 'object'
    && err !== null
    && (err as Error).name === 'ApiClientError';
}

/** Retriable when there is no HTTP body or the status is 408, 429, or 5xx. */
function isTransientTokenRequestFailure(err: unknown): boolean {
  if (isApiClientError(err)) return false;
  const status = getHttpStatus(err);
  if (status === undefined) return true;
  return status === 408 || status === 429 || status >= 500;
}

function readInitialJoinCreds(liveKitJoin: LiveKitJoinHandle | null | undefined): LiveKitJoinCreds | null {
  if (!liveKitJoin) return null;
  const cacheKey = getLiveKitJoinCacheKey(liveKitJoin);
  return cacheKey ? readCachedJoinCreds(cacheKey) : null;
}

/** Schedule refetch ~60s before expiry; remint immediately when already inside that window. */
export function scheduleRemintBeforeExpiry(
  expiresAt: string,
  onRemint: () => void,
): () => void {
  const expiryMs = Date.parse(expiresAt);
  if (Number.isNaN(expiryMs)) {
    return () => {};
  }
  const delayMs = expiryMs - Date.now() - REMINT_BEFORE_EXPIRY_MS;
  const timerId = setTimeout(onRemint, Math.max(0, delayMs));
  return () => clearTimeout(timerId);
}

/** Join errors with dedicated in-session UI — suppress generic error toasts. */
const ORAL_EXAM_JOIN_DEDICATED_UI_ERROR_CODES = new Set([
  'too_early',
  'late_join_cutoff',
  'session_started_without_you',
  'expired',
  'session_already_ended',
]);

function hasDedicatedJoinErrorUi(err: unknown): boolean {
  const code = getJoinErrorCode(err);
  return code != null && ORAL_EXAM_JOIN_DEDICATED_UI_ERROR_CODES.has(code);
}

/**
 * Request a LiveKit join token once per cache key (booking/slot) and requestId.
 * The liveKitJoin object is read from a ref so parent re-renders that rebuild
 * the handle do not cancel in-flight work or scheduled retries.
 *
 * reportApiError and t are captured via refs instead of being listed as
 * useEffect deps. Both change reference on every render, which would restart
 * the token request on each re-render and create an infinite loop when the
 * toast state update triggers another re-render.
 *
 * Returns joinError as the actual Error/ApiClientError instance so callers can
 * inspect error.code and error.details (e.g. for dedicated join-state screens).
 * Generic error toasts are suppressed for too_early, late_join_cutoff,
 * session_started_without_you, and expired so OralExamLiveSession can render its own UI.
 *
 * Schedules automatic remint ~60s before `expiresAt` so pre-flight stay TTL
 * can extend tokens that initially used the join-window expiry. Call refetch()
 * manually for connection loss or phase transitions.
 */
export function useLiveKitTokenRequest(liveKitJoin: LiveKitJoinHandle | null | undefined) {
  const { t } = useTranslation();
  const { reportApiError } = useApiErrorReporter();
  const cacheKey = liveKitJoin ? getLiveKitJoinCacheKey(liveKitJoin) : null;
  /** Stable booking/slot key when present; otherwise the handle (or null) so keyless joins still run. */
  const handleEffectKey = cacheKey ?? liveKitJoin ?? null;
  const [joinCreds, setJoinCreds] = useState<LiveKitJoinCreds | null>(
    () => readInitialJoinCreds(liveKitJoin),
  );
  const [joinError, setJoinError] = useState<Error | null>(null);
  const [requestId, setRequestId] = useState(0);

  const reportApiErrorRef = useRef(reportApiError);
  const tRef = useRef(t);
  useEffect(() => { reportApiErrorRef.current = reportApiError; }, [reportApiError]);
  useEffect(() => { tRef.current = t; }, [t]);

  const pendingRequestRef = useRef<{ requestId: number; startedAt: number } | null>(null);
  const joinCredsRef = useRef(joinCreds);
  joinCredsRef.current = joinCreds;
  const transientRetryAttemptRef = useRef(0);
  const transientRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTransientRetryTimer = useCallback(() => {
    if (transientRetryTimerRef.current != null) {
      clearTimeout(transientRetryTimerRef.current);
      transientRetryTimerRef.current = null;
    }
  }, []);

  const refetch = useCallback(() => {
    // Soft remint: keep previous creds until the new token arrives so room
    // switches never flash a blank Connecting screen mid-session.
    clearTransientRetryTimer();
    transientRetryAttemptRef.current = 0;
    setJoinError(null);
    setRequestId((id) => id + 1);
  }, [clearTransientRetryTimer]);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const liveKitJoinRef = useRef(liveKitJoin);
  liveKitJoinRef.current = liveKitJoin;

  const retry = useCallback(() => {
    const pending = pendingRequestRef.current;
    if (pending && Date.now() - pending.startedAt < RETRY_THROTTLE_MS) {
      return;
    }
    refetch();
  }, [refetch]);

  /** Ends in-session quiet remint retries once LiveKit is connected again. */
  const clearInSessionTokenRetryWait = useCallback(() => {
    clearTransientRetryTimer();
    transientRetryAttemptRef.current = 0;
  }, [clearTransientRetryTimer]);

  useEffect(() => {
    const handle = liveKitJoinRef.current;
    if (!handle) return undefined;

    const activeCacheKey = getLiveKitJoinCacheKey(handle);

    // Remount / Fast Refresh: keep the cached token and avoid reminting unless refetch().
    if (requestId === 0 && activeCacheKey && readCachedJoinCreds(activeCacheKey)) {
      return undefined;
    }

    let cancelled = false;
    const thisRequestId = requestId;
    const startedAt = Date.now();
    pendingRequestRef.current = { requestId: thisRequestId, startedAt };

    const finalizeJoinFailure = (err: unknown) => {
      const error = err instanceof Error ? err : new Error(String(err));
      setJoinError(error);

      if (!hasDedicatedJoinErrorUi(err)) {
        reportApiErrorRef.current(err, tRef.current('oralExam.joinRoom'), 'toast', {
          expectedErrorCodes: [...ORAL_EXAM_JOIN_EXPECTED_ERROR_CODES],
        });
      }
    };

    handle.requestToken()
      .then((res) => {
        if (cancelled) return;
        transientRetryAttemptRef.current = 0;
        const creds: LiveKitJoinCreds = {
          token: res.token,
          livekitUrl: res.livekitUrl,
          ...(res.roomName ? { roomName: res.roomName } : {}),
          ...(res.expiresAt ? { expiresAt: res.expiresAt } : {}),
        };
        if (activeCacheKey) writeCachedJoinCreds(activeCacheKey, creds);
        setJoinCreds(creds);
      })
      .catch((err: unknown) => {
        if (cancelled) return;

        const hadJoinCreds = joinCredsRef.current != null;
        if (hadJoinCreds && isTransientTokenRequestFailure(err)) {
          const attempt = transientRetryAttemptRef.current;
          if (attempt < TRANSIENT_TOKEN_RETRY_DELAYS_MS.length) {
            transientRetryAttemptRef.current = attempt + 1;
            const delayMs = TRANSIENT_TOKEN_RETRY_DELAYS_MS[attempt];
            transientRetryTimerRef.current = setTimeout(() => {
              transientRetryTimerRef.current = null;
              setRequestId((id) => id + 1);
            }, delayMs);
            return;
          }
        }

        finalizeJoinFailure(err);
      })
      .finally(() => {
        if (pendingRequestRef.current?.requestId === thisRequestId) {
          pendingRequestRef.current = null;
        }
      });

    return () => {
      cancelled = true;
      clearTransientRetryTimer();
    };
  }, [handleEffectKey, clearTransientRetryTimer, requestId]); // requestId triggers refetch

  useEffect(() => {
    const expiresAt = joinCreds?.expiresAt;
    if (!expiresAt) return undefined;
    return scheduleRemintBeforeExpiry(expiresAt, () => refetchRef.current());
  }, [joinCreds?.expiresAt, joinCreds?.token]);

  const isLoading = Boolean(liveKitJoin) && !joinCreds && !joinError;

  return {
    joinCreds,
    joinError,
    refetch,
    retry,
    isLoading,
    clearInSessionTokenRetryWait,
  };
}
